import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PageStyleBaseComponent } from 'app/shared/components/base/page-style-base/page-style-base.component';
import {
  HostedPaymentBank,
  HostedPaymentBankFlowDefinitionRequiredDataField,
  HostedPaymentCountry,
  HostedPaymentRequest,
  HostedPaymentRequestStart,
  HostedPaymentRequestStartResponse
} from 'app/shared/models/hosted-payment-request.model';
import { PageStyleService } from 'app/shared/services/page-style.service';
import { PaymentService } from 'app/shared/services/payment.service';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-hosted-payment-request',
  templateUrl: './hosted-payment-request.component.html',
  styleUrls: ['./hosted-payment-request.component.scss']
})
export class HostedPaymentRequestComponent extends PageStyleBaseComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private customStyleElement: HTMLStyleElement | null = null;
  private closeOptionsTimeout: any = null;
  private countrySelectionBeforeEdit: HostedPaymentCountry | null = null;
  private bankSelectionBeforeEdit: HostedPaymentBank | null = null;

  authorizationCode: string;
  paymentRequest: HostedPaymentRequest;

  countries: HostedPaymentCountry[] = [];
  banks: HostedPaymentBank[] = [];
  filteredCountries: HostedPaymentCountry[] = [];
  filteredBanks: HostedPaymentBank[] = [];

  selectedCountry: HostedPaymentCountry | null = null;
  selectedBank: HostedPaymentBank | null = null;
  countryInput = '';
  bankInput = '';
  additionalFieldInputs: {[key: string]: string} = {};

  isLoading = true;
  isStarting = false;
  showStartError = false;
  showCountryOptions = false;
  showBankOptions = false;
  activeCountryIndex = -1;
  activeBankIndex = -1;
  isCountryFocused = false;
  isBankFocused = false;
  bankRedirectUrl: string | null = null;

  constructor(
    protected paymentService: PaymentService,
    protected pageStyleService: PageStyleService,
    private route: ActivatedRoute,
    private elementRef: ElementRef,
    private translateService: TranslateService
  ) {
    super(pageStyleService);
  }

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.authorizationCode = params.authorizationCode;
        this.loadPaymentRequest();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cancelOptionsClose();

    if (this.customStyleElement) {
      document.head.removeChild(this.customStyleElement);
    }
  }

  get isPaymentValid(): boolean {
    return this.paymentRequest &&
      this.paymentRequest.IsValid &&
      !this.paymentRequest.IsExpired &&
      !this.paymentRequest.IsCompleted &&
      !this.paymentRequest.IsInProgress;
  }

  get isPaymentInProgress(): boolean {
    return this.paymentRequest &&
      this.paymentRequest.IsValid &&
      this.paymentRequest.IsInProgress &&
      !this.paymentRequest.IsExpired &&
      !this.paymentRequest.IsCompleted;
  }

  get canStartPayment(): boolean {
    return this.isPaymentValid &&
      !!this.selectedCountry &&
      !!this.selectedBank &&
      this.areAdditionalFieldsValid() &&
      !this.isStarting &&
      !this.bankRedirectUrl;
  }

  get hasCountryValidationState(): boolean {
    return !!this.countryInput || this.isCountryFocused;
  }

  get isCountryValid(): boolean {
    return !!this.selectedCountry && this.countryInput === this.selectedCountry.Name;
  }

  get hasBankValidationState(): boolean {
    return !!this.bankInput || this.isBankFocused;
  }

  get isBankValid(): boolean {
    return !!this.selectedBank && this.bankInput === this.selectedBank.Name;
  }

  get paymentLogoImageUrl(): any {
    return this.logoImageUrl || this.getTemplateLogoImageUrl();
  }

  get shouldShowCountryOptions(): boolean {
    return this.showCountryOptions && this.shouldShowOptions(this.countryInput, this.filteredCountries, item => item.Name);
  }

  get shouldShowBankOptions(): boolean {
    return this.showBankOptions && this.shouldShowOptions(this.bankInput, this.filteredBanks, item => item.Name);
  }

  get activeCountryOptionId(): string | null {
    return this.shouldShowCountryOptions && this.activeCountryIndex >= 0
      ? `aihub-payment-request-country-option-${this.activeCountryIndex}`
      : null;
  }

  get activeBankOptionId(): string | null {
    return this.shouldShowBankOptions && this.activeBankIndex >= 0
      ? `aihub-payment-request-bank-option-${this.activeBankIndex}`
      : null;
  }

  get isCountryLabelRaised(): boolean {
    return this.isCountryFocused || this.isCountryValid;
  }

  get isBankLabelRaised(): boolean {
    return this.isBankFocused || this.isBankValid;
  }

  get selectedBankName(): string {
    return this.selectedBank ? this.selectedBank.Name : 'uw bank';
  }

  get selectedBankAdditionalFields(): HostedPaymentBankFlowDefinitionRequiredDataField[] {
    if (!this.selectedBank) {
      return [];
    }

    const paymentInitiationFlowDefinition = this.getPaymentInitiationFlowDefinition(this.selectedBank);
    const requiredData = paymentInitiationFlowDefinition?.RequiredData || {};

    return Object.keys(requiredData)
      .map(key => requiredData[key])
      .filter(field => field && field.Required);
  }

  loadPaymentRequest(): void {
    this.isLoading = true;
    this.showStartError = false;
    this.bankRedirectUrl = null;

    super.initialize(this.authorizationCode, () => {
      this.initializeStyles();
      this.applyCustomCss();
    });

    this.paymentService.getHostedPaymentRequest(this.authorizationCode, (paymentRequest: HostedPaymentRequest) => {
      if (!paymentRequest) {
        this.isLoading = false;
        return;
      }

      this.paymentRequest = paymentRequest;
      if (paymentRequest.PageStyle) {
        this.pageStyle = paymentRequest.PageStyle;
        this.initializeStyles();
        this.applyCustomCss();
      }

      if (!this.isPaymentValid && !this.isPaymentInProgress && paymentRequest.RedirectUrl) {
        window.location.href = paymentRequest.RedirectUrl;
        return;
      }

      if (this.isPaymentValid) {
        this.loadCountries();
      }

      this.isLoading = false;
    });
  }

  loadCountries(): void {
    this.paymentService.getHostedPaymentCountries((countries: HostedPaymentCountry[]) => {
      this.countries = (countries || []).map(country => this.normalizeCountry(country));
      this.filteredCountries = this.countries;

      const defaultCountry = this.countries.find(country => country.Id === 'NL');
      if (defaultCountry) {
        this.selectCountry(defaultCountry);
      }
    });
  }

  onCountryInput(value: string): void {
    this.cancelOptionsClose();
    this.countryInput = value;
    this.showCountryOptions = false;
    this.activeCountryIndex = -1;
    this.selectedCountry = null;
    this.selectedBank = null;
    this.bankInput = '';
    this.additionalFieldInputs = {};
    this.banks = [];
    this.filteredBanks = [];

    const search = value.toLowerCase();
    this.filteredCountries = this.countries.filter(country =>
      country.Name.toLowerCase().includes(search) || country.Id.toLowerCase().includes(search));

    const exactCountry = this.countries.find(country => country.Name === value || country.Id === value);
    if (exactCountry) {
      this.selectCountry(exactCountry);
      return;
    }

    this.showCountryOptions = this.shouldShowOptions(this.countryInput, this.filteredCountries, item => item.Name);
  }

  onBankInput(value: string): void {
    this.cancelOptionsClose();
    this.bankInput = value;
    this.showBankOptions = false;
    this.activeBankIndex = -1;
    this.selectedBank = null;
    this.additionalFieldInputs = {};

    const search = value.toLowerCase();
    this.filteredBanks = this.banks.filter(bank =>
      bank.Name.toLowerCase().includes(search) || bank.BankId.toLowerCase().includes(search));

    const exactBank = this.banks.find(bank => bank.Name === value || bank.BankId === value);
    if (exactBank) {
      this.selectBank(exactBank);
      return;
    }

    this.showBankOptions = this.shouldShowOptions(this.bankInput, this.filteredBanks, item => item.Name);
  }

  selectCountry(country: HostedPaymentCountry): void {
    this.cancelOptionsClose();
    this.selectedCountry = country;
    this.countryInput = country.Name;
    this.showCountryOptions = false;
    this.activeCountryIndex = -1;
    this.selectedBank = null;
    this.bankInput = '';
    this.additionalFieldInputs = {};

    this.paymentService.getHostedPaymentBanks(country.Id, (banks: HostedPaymentBank[]) => {
      this.banks = (banks || [])
        .map(bank => this.normalizeBank(bank))
        .filter(bank => this.isBankSupportedForRedirectPaymentInitiation(bank));
      this.filteredBanks = this.banks;
    });
  }

  selectBank(bank: HostedPaymentBank): void {
    this.cancelOptionsClose();
    this.selectedBank = bank;
    this.bankInput = bank.Name;
    this.additionalFieldInputs = {};
    this.selectedBankAdditionalFields.forEach(field => {
      this.additionalFieldInputs[field.Name] = field.DefaultValue || '';
    });
    this.showBankOptions = false;
    this.activeBankIndex = -1;
  }

  onCountryFocus(): void {
    this.cancelOptionsClose();
    this.isCountryFocused = true;
    this.countrySelectionBeforeEdit = this.selectedCountry;
    this.bankSelectionBeforeEdit = this.selectedBank;
    this.countryInput = '';
    this.filteredCountries = this.countries;
    this.showCountryOptions = this.shouldShowOptions(this.countryInput, this.filteredCountries, item => item.Name);
    this.showBankOptions = false;
    this.activeCountryIndex = -1;
  }

  onBankFocus(): void {
    this.cancelOptionsClose();
    this.isBankFocused = true;
    this.bankSelectionBeforeEdit = this.selectedBank;
    if (this.selectedCountry) {
      this.bankInput = '';
      this.filteredBanks = this.banks;
      this.showBankOptions = this.shouldShowOptions(this.bankInput, this.filteredBanks, item => item.Name);
      this.showCountryOptions = false;
      this.activeBankIndex = -1;
    }
  }

  closeCountryOptionsAfterDelay(): void {
    this.cancelOptionsClose();
    this.closeOptionsTimeout = setTimeout(() => {
      this.isCountryFocused = false;
      this.showCountryOptions = false;
      this.activeCountryIndex = -1;
      this.revertInvalidCountrySelection();
      this.closeOptionsTimeout = null;
    }, 150);
  }

  closeBankOptionsAfterDelay(): void {
    this.cancelOptionsClose();
    this.closeOptionsTimeout = setTimeout(() => {
      this.isBankFocused = false;
      this.showBankOptions = false;
      this.activeBankIndex = -1;
      this.revertInvalidBankSelection();
      this.closeOptionsTimeout = null;
    }, 150);
  }

  onCountryKeyDown(event: KeyboardEvent): void {
    this.handleOptionsKeyDown(
      event,
      this.filteredCountries,
      () => this.shouldShowCountryOptions,
      () => this.showCountryOptions = this.shouldShowOptions(this.countryInput, this.filteredCountries, item => item.Name),
      () => this.activeCountryIndex,
      index => this.activeCountryIndex = index,
      country => this.selectCountry(country));
  }

  onBankKeyDown(event: KeyboardEvent): void {
    this.handleOptionsKeyDown(
      event,
      this.filteredBanks,
      () => this.shouldShowBankOptions,
      () => this.showBankOptions = this.shouldShowOptions(this.bankInput, this.filteredBanks, item => item.Name),
      () => this.activeBankIndex,
      index => this.activeBankIndex = index,
      bank => this.selectBank(bank));
  }

  startPayment(): void {
    if (!this.canStartPayment) {
      return;
    }

    this.isStarting = true;
    this.showStartError = false;

    const model = new HostedPaymentRequestStart();
    model.CountryId = this.selectedCountry.Id;
    model.BankId = this.selectedBank.BankId;
    model.AdditionalFields = this.getAdditionalFieldsPayload();

    this.paymentService.startHostedPaymentRequest(this.authorizationCode, model, (response: HostedPaymentRequestStartResponse) => {
      if (response && response.UiUrlRedirect) {
        this.bankRedirectUrl = response.UiUrlRedirect;
        this.isStarting = false;
        return;
      }

      this.isStarting = false;
      this.showStartError = true;
    });
  }

  continueToBank(): void {
    if (!this.bankRedirectUrl) {
      return;
    }

    window.location.href = this.bankRedirectUrl;
  }

  formatAmount(amount: number): string {
    return (amount || 0).toLocaleString('nl-NL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  formatDate(value: Date): string {
    if (!value) {
      return '';
    }

    return new Date(value).toLocaleDateString('nl-NL');
  }

  private applyCustomCss(): void {
    if (this.customStyleElement) {
      document.head.removeChild(this.customStyleElement);
      this.customStyleElement = null;
    }

    if (!this.pageStyle || !this.pageStyle.AIHubPaymentRequestCss) {
      return;
    }

    const element = this.elementRef.nativeElement;
    const ngContentAttribute: any = Array.from(element.attributes)[0];
    const ngContentName = ngContentAttribute ? ngContentAttribute.name : null;
    let scopedCss = this.normalizeCustomCss(this.pageStyle.AIHubPaymentRequestCss);

    if (ngContentName) {
      scopedCss = scopedCss.replace(/(\.aihub-payment-request[-_a-zA-Z0-9 ]*\{)/g, `[${ngContentName}] $1`);
    }

    this.customStyleElement = document.createElement('style');
    this.customStyleElement.textContent = scopedCss;
    document.head.appendChild(this.customStyleElement);
  }

  private normalizeCustomCss(css: string): string {
    return css.replace(
      /(\.aihub-payment-request-page\s*\{[^}]*)height\s*:\s*100vh\s*;?/gi,
      '$1min-height: 100vh; height: auto !important;'
    );
  }

  private normalizeCountry(country: any): HostedPaymentCountry {
    const id = country.Id || country.id || country.CountryId || country.countryId;
    const fallbackName = country.Name || country.name || country.DisplayName || country.displayName;

    return {
      Id: id,
      Name: this.getTranslatedCountryName(id, fallbackName)
    } as HostedPaymentCountry;
  }

  private getTranslatedCountryName(countryId: string, fallbackName: string): string {
    const translationKey = `ibanXS.countries.${countryId}`;
    const translatedName = this.translateService.instant(translationKey);

    return translatedName && translatedName !== translationKey ? translatedName : fallbackName;
  }

  private normalizeBank(bank: any): HostedPaymentBank {
    return {
      BankId: bank.BankId || bank.bankId || bank.Id || bank.id,
      Name: bank.Name || bank.name || bank.DisplayName || bank.displayName,
      Services: bank.Services || bank.services || [],
      FlowDefinitions: this.normalizeFlowDefinitions(bank.FlowDefinitions || bank.flowDefinitions || []),
      Bic: bank.Bic || bank.bic || []
    } as HostedPaymentBank;
  }

  private isBankSupportedForRedirectPaymentInitiation(bank: HostedPaymentBank): boolean {
    const supportsPaymentInitiation = !bank.Services ||
      bank.Services.some(service => service.toLowerCase() === 'pis');

    if (!supportsPaymentInitiation) {
      return false;
    }

    if (!bank.FlowDefinitions || Object.keys(bank.FlowDefinitions).length === 0) {
      return true;
    }

    const paymentInitiationFlowDefinition = this.getPaymentInitiationFlowDefinition(bank);

    if (!paymentInitiationFlowDefinition) {
      return false;
    }

    const supportsRedirectFlow = !paymentInitiationFlowDefinition.Specification ||
      !paymentInitiationFlowDefinition.Specification.AvailableModes ||
      paymentInitiationFlowDefinition.Specification.AvailableModes.length === 0 ||
      paymentInitiationFlowDefinition.Specification.AvailableModes.some(mode => mode.toLowerCase() === 'redirect');

    const isPremium = paymentInitiationFlowDefinition.Specification &&
      paymentInitiationFlowDefinition.Specification.AvailableModes &&
      paymentInitiationFlowDefinition.Specification.AvailableModes.some(mode => mode.toLowerCase() === 'premium');

    return supportsRedirectFlow && !isPremium;
  }

  getAdditionalFieldLabel(field: HostedPaymentBankFlowDefinitionRequiredDataField): string {
    return field.Label || field.DisplayName || field.Name;
  }

  getAdditionalFieldType(field: HostedPaymentBankFlowDefinitionRequiredDataField): string {
    const fieldEnum = (field.FieldEnum || '').toLowerCase();

    if (fieldEnum === 'amount') {
      return 'number';
    }

    return 'text';
  }

  isAdditionalFieldValid(field: HostedPaymentBankFlowDefinitionRequiredDataField): boolean {
    const value = (this.additionalFieldInputs[field.Name] || '').trim();

    if (field.Required && !value) {
      return false;
    }

    if (value && ((field.Name || '').toLowerCase() === 'debtoriban' || (field.FieldEnum || '').toLowerCase() === 'iban')) {
      return this.isValidIban(value);
    }

    if (value && field.DefaultAvailableValues && field.DefaultAvailableValues.length > 0) {
      return field.DefaultAvailableValues.some(allowedValue => allowedValue.toLowerCase() === value.toLowerCase());
    }

    return true;
  }

  hasAdditionalFieldValidationState(field: HostedPaymentBankFlowDefinitionRequiredDataField): boolean {
    return !!(this.additionalFieldInputs[field.Name] || '').trim();
  }

  private getPaymentInitiationFlowDefinition(bank: HostedPaymentBank): any {
    if (!bank || !bank.FlowDefinitions) {
      return null;
    }

    const paymentInitiationFlowDefinitionKey = Object.keys(bank.FlowDefinitions)
      .find(key => key.toLowerCase() === 'pis');

    return paymentInitiationFlowDefinitionKey
      ? bank.FlowDefinitions[paymentInitiationFlowDefinitionKey]
      : null;
  }

  private normalizeFlowDefinitions(flowDefinitions: any): any {
    if (!flowDefinitions) {
      return {};
    }

    if (Array.isArray(flowDefinitions)) {
      return flowDefinitions.reduce((normalized, flowDefinition) => {
        const key = flowDefinition.FlowTypeKey || flowDefinition.flowTypeKey;
        if (key) {
          normalized[key] = this.normalizeFlowDefinition(flowDefinition);
        }
        return normalized;
      }, {});
    }

    return Object.keys(flowDefinitions).reduce((normalized, key) => {
      normalized[key] = this.normalizeFlowDefinition(flowDefinitions[key]);
      return normalized;
    }, {});
  }

  private normalizeFlowDefinition(flowDefinition: any): any {
    return {
      Specification: {
        AvailableModes: flowDefinition?.Specification?.AvailableModes ||
          flowDefinition?.specification?.availableModes ||
          []
      },
      RequiredData: this.normalizeRequiredData(flowDefinition?.RequiredData || flowDefinition?.requiredData || {})
    };
  }

  private normalizeRequiredData(requiredData: any): any {
    if (!requiredData) {
      return {};
    }

    if (Array.isArray(requiredData)) {
      return requiredData.reduce((normalized, field) => {
        const key = field.Name || field.name || field.FieldEnum || field.fieldEnum;
        if (key) {
          normalized[key] = this.normalizeRequiredDataField(key, field);
        }
        return normalized;
      }, {});
    }

    return Object.keys(requiredData).reduce((normalized, key) => {
      normalized[key] = this.normalizeRequiredDataField(key, requiredData[key]);
      return normalized;
    }, {});
  }

  private normalizeRequiredDataField(key: string, field: any): any {
    return {
      Name: key,
      DisplayName: field?.DisplayName || field?.displayName || field?.Name || field?.name,
      Label: field?.Label || field?.label,
      FieldEnum: field?.FieldEnum || field?.fieldEnum,
      DefaultValue: field?.DefaultValue !== undefined ? field.DefaultValue : field?.defaultValue,
      DefaultAvailableValues: field?.DefaultAvailableValues || field?.defaultAvailableValues || [],
      Description: field?.Description || field?.description,
      Required: field?.Required !== undefined ? field.Required : field?.required
    };
  }

  private getTemplateLogoImageUrl(): string | null {
    const html = this.pageStyle && (this.pageStyle.AIHubPaymentRequestHtml || this.pageStyle.Html);
    if (!html) {
      return null;
    }

    const match = html.match(/<img[^>]+src=["']([^"']+)["']/i);
    return match ? match[1] : null;
  }

  private shouldShowOptions<T>(value: string, items: T[], getName: (item: T) => string): boolean {
    if (!items || items.length === 0) {
      return false;
    }

    const normalizedValue = (value || '').trim().toLowerCase();
    return !items.some(item => (getName(item) || '').toLowerCase() === normalizedValue);
  }

  private revertInvalidCountrySelection(): void {
    if (this.isCountryValid) {
      return;
    }

    this.selectedCountry = this.countrySelectionBeforeEdit;
    this.selectedBank = this.bankSelectionBeforeEdit;
    this.countryInput = this.selectedCountry ? this.selectedCountry.Name : '';
    this.bankInput = this.selectedBank ? this.selectedBank.Name : '';
    this.filteredCountries = this.countries;

    if (this.selectedCountry && this.banks.length === 0) {
      this.paymentService.getHostedPaymentBanks(this.selectedCountry.Id, (banks: HostedPaymentBank[]) => {
        this.banks = (banks || [])
          .map(bank => this.normalizeBank(bank))
          .filter(bank => this.isBankSupportedForRedirectPaymentInitiation(bank));
        this.filteredBanks = this.banks;
      });
    }
  }

  private revertInvalidBankSelection(): void {
    if (this.isBankValid) {
      return;
    }

    this.selectedBank = this.bankSelectionBeforeEdit;
    this.bankInput = this.selectedBank ? this.selectedBank.Name : '';
    this.additionalFieldInputs = {};
    if (this.selectedBank) {
      this.selectedBankAdditionalFields.forEach(field => {
        this.additionalFieldInputs[field.Name] = field.DefaultValue || '';
      });
    }
    this.filteredBanks = this.banks;
  }

  private areAdditionalFieldsValid(): boolean {
    return this.selectedBankAdditionalFields.every(field => this.isAdditionalFieldValid(field));
  }

  private getAdditionalFieldsPayload(): {[key: string]: string} {
    return this.selectedBankAdditionalFields.reduce((payload: {[key: string]: string}, field) => {
      payload[field.Name] = this.normalizeAdditionalFieldValue(field, this.additionalFieldInputs[field.Name]);
      return payload;
    }, {});
  }

  private normalizeAdditionalFieldValue(field: HostedPaymentBankFlowDefinitionRequiredDataField, value: string): string {
    value = (value || '').trim();

    if ((field.Name || '').toLowerCase() === 'debtoriban' || (field.FieldEnum || '').toLowerCase() === 'iban') {
      return value.replace(/\s+/g, '').toUpperCase();
    }

    return value;
  }

  private isValidIban(value: string): boolean {
    value = (value || '').replace(/\s+/g, '').toUpperCase();
    if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(value)) {
      return false;
    }

    const rearranged = value.slice(4) + value.slice(0, 4);
    let remainder = 0;
    for (const character of rearranged) {
      const code = character.charCodeAt(0);
      if (code >= 48 && code <= 57) {
        remainder = (remainder * 10 + (code - 48)) % 97;
      } else if (code >= 65 && code <= 90) {
        remainder = (remainder * 100 + (code - 55)) % 97;
      } else {
        return false;
      }
    }

    return remainder === 1;
  }

  private cancelOptionsClose(): void {
    if (this.closeOptionsTimeout) {
      clearTimeout(this.closeOptionsTimeout);
      this.closeOptionsTimeout = null;
    }
  }

  private handleOptionsKeyDown<T>(
    event: KeyboardEvent,
    items: T[],
    isOpen: () => boolean,
    open: () => void,
    getActiveIndex: () => number,
    setActiveIndex: (index: number) => void,
    select: (item: T) => void): void {
    if (!items || items.length === 0) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen()) {
          open();
        }
        setActiveIndex(Math.min(getActiveIndex() + 1, items.length - 1));
        this.scrollActiveOptionIntoView();
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!isOpen()) {
          open();
        }
        setActiveIndex(Math.max(getActiveIndex() - 1, 0));
        this.scrollActiveOptionIntoView();
        break;
      case 'Enter':
        if (isOpen() && getActiveIndex() >= 0) {
          event.preventDefault();
          select(items[getActiveIndex()]);
        }
        break;
      case 'Escape':
        this.showCountryOptions = false;
        this.showBankOptions = false;
        this.activeCountryIndex = -1;
        this.activeBankIndex = -1;
        break;
    }
  }

  private scrollActiveOptionIntoView(): void {
    setTimeout(() => {
      const activeOption = this.elementRef.nativeElement.querySelector('.aihub-payment-request-option.active');
      if (activeOption) {
        activeOption.scrollIntoView({ block: 'nearest' });
      }
    });
  }
}
